import React, {useEffect, useState} from "react";
import ScoreStarsBox from "../../../component/global/ScoreStarsBox";
import {roundNumber} from "../../../utils/methods";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import {useTranslation} from "react-i18next";
import face1 from "./../../../asset/icons/face-1.png";
import face2 from "./../../../asset/icons/face-2.png";
import face3 from "./../../../asset/icons/face-3.png";
import face4 from "./../../../asset/icons/face-4.png";
import face5 from "./../../../asset/icons/face-5.png";
import {
  attachmentHref,
  guessAudioMime,
  resolveMediaUrl,
} from "../../../utils/mediaUrl";
import {
  combinedOverallAverage,
  weightedCriteriaAverage,
} from "../commentScores";

const clampScore = (score) => {
  const parsedScore = Number(score);
  return Number.isFinite(parsedScore) ? Math.min(Math.max(parsedScore, 0), 5) : 0;
};

const FeedbackMedia = ({files = [], voice}) => {
  const {t} = useTranslation();
  const voiceSrc = voice ? resolveMediaUrl(voice) : null;
  const voiceType = guessAudioMime(voiceSrc);
  const attachments = files
    .map((file) => attachmentHref(file))
    .filter(Boolean);

  if (!attachments.length && !voiceSrc) return null;

  return (
    <div className="mm-feedback-media">
      {attachments.map((href, index) => (
        <a
          className="mm-attachment"
          href={href}
          target="_blank"
          rel="noreferrer"
          key={`${href}-${index}`}
        >
          <span aria-hidden="true">↗</span>
          {t("Organizational.Messages.Pictures.Tab")} {index + 1}
        </a>
      ))}
      {voiceSrc && (
        <div className="mm-voice">
          <AudioPlayer
            src={voiceSrc}
            showJumpControls={false}
            className="my-player"
          />
          <a
            className="mm-voice__download"
            href={voiceSrc}
            download
            type={voiceType}
          >
            {t("Organizational.Messages.Download.Label")}
          </a>
        </div>
      )}
    </div>
  );
};

const CriteriaItem = ({criteria, description, computed_score}) => {
  const {t} = useTranslation();
  const criteriaName =
    criteria?.criteria?.name || criteria?.name || "";
  const normalizedScore = clampScore(computed_score);

  return (
    <article className="mm-criterion">
      <header className="mm-criterion__header">
        <h3>{criteriaName}</h3>
        <div className="mm-criterion__score">
          <strong>{roundNumber(normalizedScore)}</strong>
          <span>
            {t("Src.Pages.Messages.Modals.CommentDetals.From")} 5
          </span>
        </div>
      </header>
      <div
        className="mm-criterion__track"
        role="progressbar"
        aria-label={criteriaName}
        aria-valuemin="0"
        aria-valuemax="5"
        aria-valuenow={normalizedScore}
      >
        <span style={{width: `${normalizedScore * 20}%`}}/>
      </div>
      {description && <p className="mm-criterion__description">{description}</p>}
    </article>
  );
};

const ScoreMetric = ({label, value}) => {
  const {t} = useTranslation();
  if (value == null) return null;
  const normalizedScore = clampScore(value);

  return (
    <p className="mm-score-metric">
      <span>{label}</span>
      <strong>{roundNumber(normalizedScore)}</strong>
      <small>
        {t("Src.Pages.Messages.Modals.CommentDetals.From")} 5
      </small>
    </p>
  );
};

const getMessageAge = (dateTime, now) => {
  const timestamp = new Date(dateTime).getTime();
  if (!Number.isFinite(timestamp)) return "—";

  const difference = Math.max(0, Math.floor((now - timestamp) / 60000));
  const hours = Math.floor(difference / 60);
  const minutes = difference % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const CommentDetals = ({
                         criteria,
                         computed_score,
                         score,
                         description,
                         voice,
                         dateTime,
                         file,
                         ai,
                       }) => {
  const [now, setNow] = useState(Date.now());
  const {t} = useTranslation();
  const primaryScore = clampScore(score ?? computed_score);
  const criteriaAverage = weightedCriteriaAverage(criteria);
  const combinedAverage = combinedOverallAverage(criteriaAverage, score);

  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [dateTime]);

  return (
    <div className="comment_detals_content modal_main_content modal_co_detail_main">
      <section className="mm-opinion" aria-labelledby="mm-opinion-title">
        <header className="mm-opinion__header">
          <div>
            <h2 id="mm-opinion-title">
              {t("Organizational.Messages.OverallFeedback.Label")}
            </h2>
            <ScoreMetric
              label={t("Organizational.Messages.CombinedAverage.Label")}
              value={combinedAverage}
            />
          </div>
          <div className="mm-opinion__age">
            <span>{t("Organizational.Messages.Message'sAge.Label")}</span>
            <strong dir="ltr">{getMessageAge(dateTime, now)}</strong>
          </div>
        </header>
        <div className="mm-opinion__body">
          <div className="mm-opinion__rating">
            <strong>{roundNumber(primaryScore)}</strong>
            <span>
              {t("Src.Pages.Messages.Modals.CommentDetals.From")} 5
            </span>
            <span dir="ltr" className="mm-stars">
              <ScoreStarsBox size="medium" count={primaryScore}/>
            </span>
          </div>
          <p className={`mm-opinion__text${description ? "" : " is-empty"}`}>
            {description ||
              t("Organizational.Messages.NoTextsReceived.Label")}
          </p>
        </div>
        <FeedbackMedia files={file ? [{file}] : []} voice={voice}/>
      </section>
      {ai && (
        <section className="ai-box">
          <h2>
            {t("Src.Pages.Messages.Modals.CommentDetals.aiHeader")}
          </h2>
          <div className="mm-sentiment" aria-label={`${ai} / 5`}>
            {[face1, face2, face3, face4, face5].map((face, index) => (
              <img
                src={face}
                alt=""
                width="32"
                height="32"
                aria-hidden="true"
                className={ai === index + 1 ? "selected" : ""}
                key={face}
              />
            ))}
          </div>
        </section>
      )}
      {(criteria || []).length > 0 && (
        <section className="mm-criteria" aria-labelledby="mm-criteria-title">
          <header className="mm-criteria__header">
            <h2 id="mm-criteria-title">
              {t("Organizational.Messages.ScoreBreakdown.Label")}
            </h2>
            <ScoreMetric
              label={t("Organizational.Messages.WeightedCriteria.Label")}
              value={criteriaAverage}
            />
          </header>
          <div className="mm-criteria__grid">
            {criteria.map((criterion, index) => (
              <CriteriaItem key={index} {...criterion} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default CommentDetals;
